import { append, h } from './dom';
import { svgIcon } from './icons';
import type { Rgb } from '@/types/engine';

export interface SliderOptions {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  format?: (v: number) => string;
  hint?: string;
  onInput: (value: number) => void;
  onChange?: (value: number) => void;
}

export interface SliderControl {
  element: HTMLElement;
  setValue(v: number): void;
  setDisabled(disabled: boolean): void;
}

export function slider(o: SliderOptions): SliderControl {
  const range = h('input', { type: 'range', min: String(o.min), max: String(o.max), step: String(o.step), value: String(o.value), class: 'ctl-range' });
  const number = h('input', { type: 'number', min: String(o.min), max: String(o.max), step: String(o.step), value: String(o.value), class: 'ctl-number' });
  const valueText = h('span', { class: 'ctl-value' }, o.format ? o.format(o.value) : String(o.value));
  const clampValue = (v: number) => Math.min(o.max, Math.max(o.min, v));
  const update = (v: number, fire: 'input' | 'change') => {
    if (!Number.isFinite(v)) return;
    const value = clampValue(v);
    range.value = String(value);
    number.value = String(value);
    valueText.textContent = o.format ? o.format(value) : String(value);
    if (fire === 'input') o.onInput(value);
    else (o.onChange ?? o.onInput)(value);
  };
  range.addEventListener('input', () => update(Number(range.value), 'input'));
  range.addEventListener('change', () => update(Number(range.value), 'change'));
  number.addEventListener('change', () => update(Number(number.value), 'change'));
  const element = h(
    'label',
    { class: 'ctl ctl-slider', title: o.hint ?? '' },
    h('span', { class: 'ctl-label' }, o.label, valueText),
    h('span', { class: 'ctl-inputs' }, range, number),
  );
  return {
    element,
    setValue(v) {
      const value = clampValue(v);
      range.value = String(value);
      number.value = String(value);
      valueText.textContent = o.format ? o.format(value) : String(value);
    },
    setDisabled(d) {
      range.disabled = d;
      number.disabled = d;
      element.classList.toggle('is-disabled', d);
    },
  };
}

export interface SelectOptions<T extends string> {
  label: string;
  value: T;
  options: Array<{ value: T; label: string }>;
  hint?: string;
  onChange: (value: T) => void;
}

export interface SelectControl<T extends string> {
  element: HTMLElement;
  setValue(v: T): void;
  setDisabled(d: boolean): void;
}

export function select<T extends string>(o: SelectOptions<T>): SelectControl<T> {
  const sel = h('select', { class: 'ctl-select' });
  for (const opt of o.options) sel.appendChild(h('option', { value: opt.value, selected: opt.value === o.value }, opt.label));
  sel.addEventListener('change', () => o.onChange(sel.value as T));
  const element = h('label', { class: 'ctl ctl-selectwrap', title: o.hint ?? '' }, h('span', { class: 'ctl-label' }, o.label), sel);
  return {
    element,
    setValue(v) {
      sel.value = v;
    },
    setDisabled(d) {
      sel.disabled = d;
    },
  };
}

export interface SegmentedOptions<T extends string> {
  value: T;
  options: Array<{ value: T; label: string; title?: string }>;
  onChange: (value: T) => void;
  size?: 'sm' | 'md';
  label?: string;
}

export interface SegmentedControl<T extends string> {
  element: HTMLElement;
  setValue(v: T): void;
  setDisabled(d: boolean): void;
}

/** A row of exclusive choices; the active one lifts off a paper track. */
export function segmented<T extends string>(o: SegmentedOptions<T>): SegmentedControl<T> {
  const buttons = o.options.map((opt) => {
    const b = h('button', { type: 'button', title: opt.title ?? '' }, opt.label);
    b.dataset.value = opt.value;
    b.addEventListener('click', () => {
      set(opt.value);
      o.onChange(opt.value);
    });
    return b;
  });
  const set = (v: T) => {
    for (const b of buttons) {
      const on = b.dataset.value === v;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-pressed', String(on));
    }
  };
  set(o.value);
  const element = h('div', { class: `segmented${o.size === 'sm' ? ' segmented--sm' : ''}`, attrs: { role: 'group', ...(o.label ? { 'aria-label': o.label } : {}) } }, ...buttons);
  return {
    element,
    setValue: set,
    setDisabled(d) {
      for (const b of buttons) b.disabled = d;
    },
  };
}

export interface CheckboxOptions {
  label: string;
  value: boolean;
  hint?: string;
  onChange: (value: boolean) => void;
}

export interface CheckboxControl {
  element: HTMLElement;
  setValue(v: boolean): void;
  setDisabled(d: boolean): void;
}

/** A boolean drawn as a switch; the real checkbox stays in the DOM for keyboard and tests. */
export function checkbox(o: CheckboxOptions): CheckboxControl {
  const input = h('input', { type: 'checkbox', checked: o.value, class: 'ctl-checkbox' });
  input.addEventListener('change', () => o.onChange(input.checked));
  const element = h('label', { class: 'ctl ctl-check', title: o.hint ?? '' }, input, h('i', { class: 'ctl-switch', attrs: { 'aria-hidden': 'true' } }), h('span', { class: 'ctl-label' }, o.label));
  return {
    element,
    setValue(v) {
      input.checked = v;
    },
    setDisabled(d) {
      input.disabled = d;
    },
  };
}

export function rgbToHex([r, g, b]: Rgb): string {
  return `#${[r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('')}`;
}

export function hexToRgb(hex: string): Rgb {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return [0, 0, 0];
  const v = parseInt(m[1], 16);
  return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
}

export interface ColorOptions {
  label: string;
  value: Rgb;
  onChange: (value: Rgb) => void;
}

export function colorInput(o: ColorOptions): { element: HTMLElement; setValue(v: Rgb): void } {
  const hex = rgbToHex(o.value);
  const input = h('input', { type: 'color', value: hex, class: 'ctl-color' });
  const hexText = h('span', { class: 'ctl-hex' }, hex);
  input.addEventListener('input', () => {
    hexText.textContent = input.value;
    o.onChange(hexToRgb(input.value));
  });
  const element = h('label', { class: 'ctl ctl-colorwrap' }, h('span', { class: 'ctl-label' }, o.label), h('span', { class: 'ctl-colorbox' }, input, hexText));
  return {
    element,
    setValue(v) {
      input.value = rgbToHex(v);
      hexText.textContent = input.value;
    },
  };
}

export interface ButtonOptions {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  title?: string;
  /** Icon markup from `icons`, or a short text glyph. */
  icon?: string;
  block?: boolean;
}

export function button(o: ButtonOptions): HTMLButtonElement {
  const variant = o.variant && o.variant !== 'secondary' ? ` btn--${o.variant}` : '';
  const btn = h('button', { type: 'button', class: `btn${variant}${o.block ? ' btn--block' : ''}`, title: o.title ?? '' });
  setButtonContent(btn, o.label, o.icon);
  btn.addEventListener('click', o.onClick);
  return btn;
}

/** Replaces a button's icon and label, keeping the label in a span so narrow layouts can hide it. */
export function setButtonContent(btn: HTMLElement, label: string, icon?: string): void {
  btn.replaceChildren();
  append(btn, [icon ? (icon.trimStart().startsWith('<svg') ? svgIcon(icon) : h('span', { class: 'btn-icon' }, icon)) : null, label ? h('span', { class: 'btn__label' }, label) : null]);
}

export interface IconButtonOptions {
  icon: string;
  title: string;
  onClick: () => void;
  danger?: boolean;
  className?: string;
}

/** Icon-only button; the title doubles as the accessible name. */
export function iconButton(o: IconButtonOptions): HTMLButtonElement {
  const btn = h(
    'button',
    { type: 'button', class: `ibtn${o.danger ? ' ibtn--danger' : ''}${o.className ? ` ${o.className}` : ''}`, title: o.title, attrs: { 'aria-label': o.title.replace(/\s*\(.*\)$/, '') } },
    svgIcon(o.icon),
  );
  btn.addEventListener('click', o.onClick);
  return btn;
}

export function section(title: string, children: HTMLElement[], options?: { collapsed?: boolean; id?: string }): HTMLElement {
  const details = h('details', { class: 'panel-section', open: !options?.collapsed, id: options?.id ?? '' }, h('summary', null, title), h('div', { class: 'panel-section-body' }, ...children));
  return details;
}

export function row(...children: Array<HTMLElement | null>): HTMLElement {
  return h('div', { class: 'ctl-row' }, ...children);
}

export function note(text: string): HTMLElement {
  return h('p', { class: 'ctl-note' }, text);
}

export function numberField(o: { label: string; value: number; min?: number; max?: number; step?: number; onChange: (v: number) => void; hint?: string }): { element: HTMLElement; setValue(v: number): void } {
  const input = h('input', { type: 'number', value: String(o.value), min: o.min !== undefined ? String(o.min) : '', max: o.max !== undefined ? String(o.max) : '', step: String(o.step ?? 1), class: 'ctl-number' });
  input.addEventListener('change', () => {
    const v = Number(input.value);
    if (Number.isFinite(v)) o.onChange(v);
  });
  const element = h('label', { class: 'ctl ctl-numberwrap', title: o.hint ?? '' }, h('span', { class: 'ctl-label' }, o.label), input);
  return {
    element,
    setValue(v) {
      input.value = String(v);
    },
  };
}
